import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { availableNowKey } from '@/features/sessions/hooks/useNowAvailableHelpliners';
import { useAuthStore } from '@/stores/authStore';

/**
 * Unified realtime channel for the whole app.
 *
 * One WebSocket per browser tab, opened once when the user is authenticated,
 * carries every server-pushed event (portal-helpline ringing, red-flag
 * alerts, notifications, …). New event types are added in two places:
 *   1. Backend `@elixaa/shared/realtime/userEvents.ts` UserEvent union
 *   2. The matching `UserEvent` mirror below
 * Then any FE component calls `useRealtimeEvent('the.type', handler)`.
 *
 * Wire protocol mirrors @elixaa/shared/realtime/userEvents:
 *   server → client : { kind:'auth.ok' } | { kind:'auth.fail', reason }
 *                   | { kind:'event', event:UserEvent }
 *   client → server : { kind:'auth', token }
 *
 * Reconnect: exponential backoff 1→2→4→8→16→30s, reset on auth.ok.
 * Token rotation: a new access token in the auth store closes the old
 * connection and opens a new one (effect dep on accessToken).
 *
 * Heartbeat: handled by the WebSocket protocol-level ping/pong frames the
 * server sends — browsers pong transparently, no app code needed. If the
 * server stops pinging (network partition), the browser's TCP keepalive
 * will eventually drop and trigger our reconnect path.
 */

// ─── Event types — mirror of UserEvent in @elixaa/shared ──────────────────
// Kept in sync by hand. Drift here = silent miss in the FE; the dispatch
// path below is type-erased on purpose so an unknown event type just no-ops.

export interface PortalHelplineIncomingEvent {
  type: 'portal-helpline.incoming';
  sessionId: string;
  clientId: string;
  clientName: string;
  /** Human-readable location name (e.g. "Main Campus"). Shown in the dialog
   *  so the psy knows which site is calling BEFORE picking up. */
  locationName: string;
  concerns: string[];
  startedAt: string;
}

type SessionChangedStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show'
  | 'rescheduled';

export interface SessionChangedEvent {
  type: 'session.changed';
  sessionId: string;
  status: SessionChangedStatus;
  // The change trigger, not the new status: 'created' = new row, 'status' =
  // generic status update (read `status`), the rest match the status just set.
  reason: 'created' | 'rescheduled' | 'status' | 'completed' | 'cancelled' | 'no_show';
  scheduledStart: string; // ISO
  scheduledEnd: string; // ISO
  clientId: string | null; // nullable — anonymous helpline/walk-in sessions
  practitionerId: string; // always present
  organizationId: string | null;
  locationId: string | null;
  updatedAt: string; // ISO — to drop stale out-of-order events if state is kept
}

export interface HelplineAvailabilityChangedEvent {
  type: 'helpline.availability-changed';
  practitionerId: string;
  role: 'psychologist' | 'volunteer';
  available: boolean; // best-effort hint — always refetch available-now
  businessAssociateId: string;
}

export interface RedFlagChangedEvent {
  type: 'red-flag.changed';
  flagId: string;
  clientId: string;
  status: 'open' | 'under_review' | 'closed'; // BE emits only 'open'/'closed' today
  flagType: 'crisis' | 'acute_risk' | 'suicidal_ideation';
  organizationId: string | null;
  locationId: string | null;
  openCount?: number; // usually absent — refetch the count
}

export interface TelephonyPoolChangedEvent {
  type: 'telephony.pool-changed';
  callId: string;
  locationId: string;
  action: 'ringing' | 'answered' | 'ended' | 'claimed' | 'dismissed' | 'reassigned';
  /** "Alice Client" for a matched client, "New caller" otherwise. Never the phone number. */
  callerLabel: string;
  occurredAt: string; // ISO
}

// ─── Chat (student-psychologist text chat) ────────────────────────────────
// Payloads carry ids and display info only, never message content; receivers
// refetch over REST on receipt.

export interface ChatSessionCreatedEvent {
  type: 'chat.session-created';
  chatSessionId: string;
  locationId: string;
  organizationId: string;
  clientDisplayName: string;
}

export interface ChatMessageEvent {
  type: 'chat.message';
  chatSessionId: string;
  messageId: string;
  senderRole: 'student' | 'psychologist' | 'system';
  senderDisplayName: string;
}

export interface ChatTypingEvent {
  type: 'chat.typing';
  chatSessionId: string;
  userId: string;
  // System never types; only real users emit typing pings.
  senderRole: 'student' | 'psychologist';
  displayName: string;
  expiresAt: string; // ISO, receivers expire the indicator locally
}

export interface ChatSessionChangedEvent {
  type: 'chat.session-changed';
  chatSessionId: string;
  status: 'active' | 'closed';
  endedBy?: 'student' | 'psychologist' | 'system_timeout';
  classification?: 'enquiry' | 'chat_session';
}

// Discriminated union of every UserEvent the FE can receive. Add new event
// interfaces above and union them here. A sentinel `never`-arm style isn't
// used so a future single-arm union still type-checks the dispatch site.
export type UserEvent =
  | PortalHelplineIncomingEvent
  | SessionChangedEvent
  | HelplineAvailabilityChangedEvent
  | RedFlagChangedEvent
  | TelephonyPoolChangedEvent
  | ChatSessionCreatedEvent
  | ChatMessageEvent
  | ChatTypingEvent
  | ChatSessionChangedEvent;

type ServerWireMessage =
  | { kind: 'auth.ok' }
  | { kind: 'auth.fail'; reason: string }
  | { kind: 'event'; event: UserEvent };

type EventHandler<T extends UserEvent['type']> = (event: Extract<UserEvent, { type: T }>) => void;

type AnyHandler = (event: UserEvent) => void;

// ─── URL derivation ───────────────────────────────────────────────────────
// VITE_REALTIME_BASE_URL is `http(s)://host:port/api/v1`. Strip the path
// suffix and swap the protocol to ws(s).
const REALTIME_HTTP_BASE: string =
  (import.meta.env.VITE_REALTIME_BASE_URL as string | undefined) ?? 'http://localhost:8801/api/v1';

function buildWsUrl(): string {
  const url = new URL(REALTIME_HTTP_BASE);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  // `/api/v1` is a REST-only namespace; the WS endpoint sits at the host root.
  url.pathname = '/ws/events';
  url.search = '';
  return url.toString();
}

// ─── Backoff ──────────────────────────────────────────────────────────────
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const AUTH_HANDSHAKE_TIMEOUT_MS = 8_000;

interface RealtimeContextValue {
  /** True after server returned `auth.ok` on the current connection. */
  isReady: boolean;
  /** Register a handler for a typed event. Returns an unregister fn. */
  register: <T extends UserEvent['type']>(type: T, handler: EventHandler<T>) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

interface RealtimeProviderProps {
  children: ReactNode;
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const userId = useAuthStore((s) => s.user?.id);

  const [isReady, setIsReady] = useState(false);
  const queryClient = useQueryClient();

  // Handlers are stored in refs so that registering / unregistering doesn't
  // trigger reconnects. The set is keyed by event type; multiple subscribers
  // for the same type all fire.
  const handlersRef = useRef<Map<UserEvent['type'], Set<AnyHandler>>>(new Map());

  // Distinguishes the very first successful handshake (mount already fetched
  // the active queries) from any later one, where events may have been missed
  // while the socket was down. Component-scoped so it SURVIVES effect re-runs:
  // a token rotation re-runs the connect effect, and its reconnect must
  // self-heal exactly like a network-drop reconnect (an effect-local flag would
  // reset to false and skip the refetch, losing events during the rotation gap
  // — the psych inbox has no poll to recover them).
  const hasAuthedOnceRef = useRef(false);

  const register = useCallback(<T extends UserEvent['type']>(type: T, handler: EventHandler<T>) => {
    const map = handlersRef.current;
    let set = map.get(type);
    if (!set) {
      set = new Set();
      map.set(type, set);
    }
    // Erase the type since the dispatch site already filters by `event.type`
    // before invoking. The cast is only at the registry boundary.
    const erased = handler as unknown as AnyHandler;
    set.add(erased);
    return () => {
      set.delete(erased);
      if (set.size === 0) map.delete(type);
    };
  }, []);

  useEffect(() => {
    if (!accessToken || !userId) {
      // No auth ⇒ no connection. The previous effect's cleanup already set
      // isReady=false on the way out, so nothing to do here. Bailing this
      // way avoids a synchronous setState-in-effect lint violation.
      return undefined;
    }

    let cancelled = false;
    let ws: WebSocket | null = null;
    let attempt = 0;
    let reconnectTimer: number | null = null;
    let handshakeTimer: number | null = null;

    const clearHandshakeTimer = () => {
      if (handshakeTimer !== null) {
        window.clearTimeout(handshakeTimer);
        handshakeTimer = null;
      }
    };

    const connect = () => {
      if (cancelled) return;
      const socket = new WebSocket(buildWsUrl());
      ws = socket;

      socket.addEventListener('open', () => {
        if (cancelled) {
          socket.close();
          return;
        }
        // Send the auth handshake; server holds the connection until this
        // arrives or the 5s server-side deadline fires.
        socket.send(JSON.stringify({ kind: 'auth', token: accessToken }));
        // Independent FE-side timeout — if the server doesn't reply in
        // AUTH_HANDSHAKE_TIMEOUT_MS we tear down and reconnect.
        handshakeTimer = window.setTimeout(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.close();
          }
        }, AUTH_HANDSHAKE_TIMEOUT_MS);
      });

      socket.addEventListener('message', (ev) => {
        if (cancelled) return;
        let parsed: ServerWireMessage;
        try {
          parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as ServerWireMessage;
        } catch {
          return;
        }
        if (parsed.kind === 'auth.ok') {
          clearHandshakeTimer();
          attempt = 0; // reset backoff on a successful round-trip
          setIsReady(true);
          // Self-heal: on a RE-handshake, refetch the realtime-backed roots so
          // any event missed while the socket was down is recovered. Default
          // refetchType:'active' means only mounted queries actually refetch.
          if (hasAuthedOnceRef.current) {
            void queryClient.invalidateQueries({ queryKey: ['sessions'] });
            void queryClient.invalidateQueries({ queryKey: ['red-flags'] });
            void queryClient.invalidateQueries({ queryKey: ['portal-helpline'] });
            void queryClient.invalidateQueries({ queryKey: ['telephony'] });
            void queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
            void queryClient.invalidateQueries({ queryKey: availableNowKey });
          }
          hasAuthedOnceRef.current = true;
          return;
        }
        if (parsed.kind === 'auth.fail') {
          // Hard auth failure (revoked / mismatched token). The auth-store
          // 401 interceptor or token refresh will eventually rotate the
          // token and our effect dep will re-run with a fresh value;
          // there's no point looping connects against a known-bad token.
          // Falling through to `close` lets the regular cleanup run.
          clearHandshakeTimer();
          setIsReady(false);
          return;
        }
        // parsed.kind === 'event' (only remaining arm in the union)
        {
          const handlers = handlersRef.current.get(parsed.event.type);
          if (!handlers || handlers.size === 0) return;
          for (const fn of handlers) {
            try {
              fn(parsed.event);
            } catch (err) {
              // A buggy handler must not break the dispatch loop or the WS.
              console.error('[realtime] handler threw', err);
            }
          }
        }
      });

      socket.addEventListener('close', (ev) => {
        clearHandshakeTimer();
        setIsReady(false);
        if (cancelled) return;
        // 4001 = auth failed (server rejected token). Don't loop — wait
        // for a token change. The auth-store 401 path handles refresh.
        if (ev.code === 4001) return;
        const delay = Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_MAX_MS);
        attempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      });

      socket.addEventListener('error', () => {
        // The browser fires `error` then `close`; the close handler does
        // the reconnect. Just suppress the unhandled-error warning.
      });
    };

    connect();

    return () => {
      cancelled = true;
      clearHandshakeTimer();
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      setIsReady(false);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        // 1000 = normal closure
        ws.close(1000, 'unmount');
      }
      ws = null;
    };
  }, [accessToken, userId, queryClient]);

  const value = useMemo<RealtimeContextValue>(() => ({ isReady, register }), [isReady, register]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/**
 * Subscribe to a typed realtime event. The handler must be stable across
 * renders (wrap in useCallback) — registering on every render would churn
 * the registry.
 */
export function useRealtimeEvent<T extends UserEvent['type']>(
  type: T,
  handler: EventHandler<T>,
): void {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtimeEvent must be used inside <RealtimeProvider>');
  }
  const { register } = ctx;
  useEffect(() => {
    const off = register(type, handler);
    return off;
  }, [register, type, handler]);
}

/** True once the WebSocket has handshaken successfully. UI may show a "live" chip. */
export function useRealtimeReady(): boolean {
  const ctx = useContext(RealtimeContext);
  return ctx?.isReady ?? false;
}
